<template>
    <div v-click-outside="clickOutside" class="a-menu">
        <slot name="activator" :on="toggle" :value="showMenu"></slot>
        <transition name="a-vertical-expand" appear>
            <div v-if="visible" class="aon-menu-panel" :style="menuStyle">
                <slot></slot>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
    alignBottom: {
        type: Boolean,
        default: false,
    },
    alignLeft: {
        type: Boolean,
        default: false,
    },
    alignRight: {
        type: Boolean,
        default: false,
    },
    alignTop: {
        type: Boolean,
        default: false,
    },
    animation: {
        type: String,
        default: 'vertical',
        validator: v =>
            Object.keys(ANIMATION_TYPES).includes(v.toUpperCase()),
    },
    offsetX: {
        type: String,
        default: '0',
    },
    offsetY: {
        type: String,
        default: '0',
    },
    show: {
        type: Boolean,
        default: false,
    },
    width: {
        type: [Number, String],
        default: 'auto',
    },
    zIndex: {
        type: Number,
        default: 101,
    },
})
const emit = defineEmits(['hide'])

const visible = ref(props.show)

const animationName = computed(() => {
    const key = this.animation.toUpperCase();
    return Object.prototype.hasOwnProperty.call(ANIMATION_TYPES, key)
        ? ANIMATION_TYPES[key]
        : ANIMATION_TYPES.VERTICAL;
})
const showMenu = computed({
    get() {
        return visible.value;
    },
    set(value) {
        if (!value) {
            emit('hide');
        }

        visible.value = value;
    }
})
const menuStyle = computed( () => {
    // Consider refactor here -BB
    const style = { 'z-index': props.zIndex };

    if (props.alignBottom) {
        style['top'] = props.offsetY;
    }

    if (props.alignLeft) {
        style['left'] = props.offsetX;
    }

    if (props.alignRight) {
        style['right'] = props.offsetX;
    }

    if (props.alignTop) {
        style['bottom'] = props.offsetY;
    }

    if (props.width) {
        const width =
            typeof props.width === 'number'
                ? `${props.width}px`
                : props.width;

        style['width'] = width;
    }

    return style;
})
watch(() => props.show, (newVal, oldVal) => {
    showMenu.value = newVal;
})
const clickOutside = () => {
    if (showMenu.value) {
        showMenu.value = false;
    }
}
const toggle = (value) => {
    visible.value = value;
}
</script>

<script>
const ANIMATION_TYPES = {
    BOUNCE: 'a-bounce',
    FADE: 'a-fade',
    VERTICAL: 'a-vertical-expand',
    ZOOM: 'a-zoom',
};

export default {
    name: 'AonMenu'
}
</script>

<style lang="scss">
.a-menu {
    position: relative;

    .aon-menu-panel {
        position: absolute;
        border: 1px solid transparent;
        border-radius: 0.25em;
        color: $grey06;
    }

    .a-bounce-enter-active {
        animation: aBounceIn 0.6s;
    }

    .a-bounce-leave-active {
        animation: aZoomOut 0.6s;
    }

    .a-fade-enter-active {
        animation: aFadeIn 0.6s cubic-bezier(0, 0, 0.58, 1);
    }

    .a-fade-leave-active {
        animation: aFadeOut 0.6s cubic-bezier(0, 0, 0.58, 1);
    }

    .a-vertical-expand-enter-active {
        transform-origin: top;
        animation: aVerticalExpand 0.3s ease-in-out;
    }

    .a-vertical-expand-leave-active {
        transform-origin: top;
        animation: aVerticalCollpase 0.4s ease-in-out;
    }

    .a-zoom-enter-active {
        animation: aZoomIn 0.3s cubic-bezier(0, 0, 0.58, 1);
    }

    .a-zoom-leave-active {
        animation: aZoomOut 0.4s cubic-bezier(0, 0, 0.58, 1);
    }

    @keyframes aBounceIn {
        0% {
            opacity: 0;
            transform: scale(0.3);
        }

        40% {
            opacity: 1;
            transform: scale(1.06);
        }

        60% {
            transform: scale(0.92);
        }

        100% {
            transform: scale(1);
        }
    }

    @keyframes aBounceOut {
        0% {
            transform: scale(1);
        }

        25% {
            transform: scale(0.95);
        }

        50% {
            opacity: 1;
            transform: scale(1.1);
        }

        100% {
            opacity: 0;
            transform: scale(0.3);
        }
    }

    @keyframes aFadeIn {
        0% {
            opacity: 0;
        }

        100% {
            opacity: 1;
        }
    }

    @keyframes aFadeOut {
        0% {
            opacity: 1;
        }

        100% {
            opacity: 0;
        }
    }

    @keyframes aVerticalExpand {
        0% {
            transform: scaleY(0);
        }

        100% {
            transform: scaleY(1);
        }
    }

    @keyframes aVerticalCollpase {
        0% {
            transform: scaleY(1);
        }

        100% {
            transform: scaleY(0);
        }
    }

    @keyframes aZoomIn {
        0% {
            opacity: 0;
            -webkit-transform: scale3d(0.3, 0.3, 0.3);
            transform: scale3d(0.3, 0.3, 0.3);
        }

        50% {
            opacity: 1;
        }
    }

    @keyframes aZoomOut {
        0% {
            opacity: 1;
        }

        50% {
            opacity: 0;
            -webkit-transform: scale3d(0.3, 0.3, 0.3);
            transform: scale3d(0.3, 0.3, 0.3);
        }

        100% {
            opacity: 0;
        }
    }
}
</style>
